import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import { useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLazyListUserShipmentPricesForReportsQuery } from "store/services/reportApi";
import { useLazyGetDDLUserListQuery } from "store/services/userApi";
import ExportUserCOCReportExcel from "./ExportUserCOCReport";

const breadcrumbs = [
  { title: "Reports", path: "/app/crm/reports/list" },
  { title: "COC Collection User Report" },
];

const UserCOCCollectionReport = () => {
  const [searchParams] = useSearchParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const { viewEntity } = useAuth();

  const [responsibleUser, setResponsibleUser] = useState(
    searchParams.get("responsibleUser") || "all"
  );

  const [fetchShipmentPrices, { data: priceRes, isLoading, isFetching }] =
    useLazyListUserShipmentPricesForReportsQuery();

  const [
    fetchUsers,
    { data: usersData, isLoading: isUsersLoading, isFetching: isUserFetching },
  ] = useLazyGetDDLUserListQuery();

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => (
        <Link
          title={row?.responsibleUserName}
          to={`/app/crm/reports/coc-report/user/${row?._id}/detail`}
          className="text-blue-600 underline line-clamp-2 dark:hover:text-blue-300"
        >
          {row?.responsibleUserName ? (
            <HighlightedText text={row?.responsibleUserName} />
          ) : (
            0
          )}
        </Link>
      ),
    },

    {
      name: "Pending",
      selector: (row) => row?.totalNotPaid || 0,
      cell: (row) =>
        row?.totalNotPaid ? `Rs.${Math.abs(row.totalNotPaid).toFixed(2)}` : 0,
      sortable: true,
      align: "right",
    },

    {
      name: "Requested",
      selector: (row) => row?.totalPaid || 0,
      cell: (row) =>
        row?.totalPaid ? `Rs. ${Math.abs(row.totalPaid).toFixed(2)}` : 0,
      sortable: true,
      align: "right",
    },

    {
      name: "Finance Collected",
      selector: (row) => row?.totalCollected || 0,
      cell: (row) =>
        row?.totalCollected
          ? `Rs. ${Math.abs(row.totalCollected).toFixed(2)}`
          : 0,
      sortable: true,
      align: "right",
    },

    {
      name: "Total",
      selector: (row) => row?.total || 0,
      cell: (row) =>
        row?.total ? `Rs. ${Math.abs(row.total).toFixed(2)}` : "N/A",
      sortable: true,
      align: "right",
    },
  ];

  useEffect(() => {
    fetchUsers({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
  }, [fetchUsers, viewEntity]);

  const userOptions = [
    { label: "All", value: "all" },
    ...(usersData?.map((user) => ({
      label: user.label || user.name,
      value: user.value || user._id,
    })) || []),
  ];

  useEffect(() => {
    fetchShipmentPrices({
      responsibleUser: responsibleUser === "all" ? undefined : responsibleUser,
      limit: perPage,
      page: currentPage,
    });
  }, [currentPage, fetchShipmentPrices, perPage, responsibleUser]);

  return (
    <div>
      <BreadcrumbLayout breadcrumbs={breadcrumbs}>
        <BreadcrumbLayout.Error />
        <BreadcrumbLayout.Paper>
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 1.5,
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ minWidth: 180 }}>
                COC Collection User Report
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ flex: 1, justifyContent: "flex-end" }}
              >
                <Autocomplete
                  size="small"
                  options={[...userOptions]}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value
                  }
                  value={userOptions.find(
                    (opt) => opt.value === responsibleUser
                  )}
                  onChange={(e, newValue) => {
                    setResponsibleUser(newValue?.value || "all");
                  }}
                  disabled={isUsersLoading || isUsersLoading || isUserFetching}
                  sx={{ minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Responsible User" />
                  )}
                />

                <ExportUserCOCReportExcel />
              </Stack>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {isLoading || isFetching ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : priceRes?.data?.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No data available for the selected filters.
                </Typography>
              </Box>
            ) : (
              <AppTable
                columns={columns}
                data={priceRes?.data ?? []}
                progressPending={isLoading || isFetching}
                paginationServer
                paginationPerPage={perPage}
                paginationTotalRows={priceRes?.totalItems ?? 0}
                paginationDefaultPage={currentPage}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
              />
            )}
          </Box>
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </div>
  );
};

export default UserCOCCollectionReport;
